import { TrashCan, Add } from '@carbon/icons-react'
import React, { useState } from 'react'
import { v4 as uuid } from 'uuid'
import axios from 'axios'
import { url } from '../../utils/Constants'
import swal from 'sweetalert2'
const SurveyCreate = () => {
    const [questions, setQuestions] = useState([])
    const [surveyName, setSurveyName] = useState('')
    const [surveyDescription, setSurveyDescription] = useState('')
    const [submitting , isSubmitting] = useState(false)

    // Get validation options based on field type
    const getValidationOptions = (fieldType) => {
        switch(fieldType) {
            case 'text':
                return [
                    { value: 'alphanumeric', label: 'Alphanumeric' },
                    { value: 'alphabet-only', label: 'Alphabet Only' }
                ]
            case 'file-upload':
                return [
                    { value: 'file-allowed-types', label: 'File Allowed Types (pdf, xls, xlsx, ppt, pptx, doc, docx, jpg, png, mp4, mp3)' }
                ]
            default:
                return []
        }
    }

    const appendQuestion = () => {
        const question = {
            id: uuid(),
            name: '',
            type: 'text',
            InputValidation: 'text',
            options: []
        }
        setQuestions([...questions, question])
    }

    const removeQuestion = (questionId) => {
        const updatedQuestions = questions.filter((question) => question.id !== questionId)
        setQuestions(updatedQuestions)
    }

    const addOptions = (questionId) => {
        const updatedQuestions = questions.map((question) => question.id === questionId ?
            {
                ...question, options: [...question.options,
                { id: uuid(), value: '' }]
            } : question) 
        setQuestions(updatedQuestions)
    }

    const RemoveOption = (questionId, optionId) => { 
        const updatedQuestions = questions.map((question) => question.id === questionId ?
            {
                ...question,
                options: question.options.filter((option) => option.id !== optionId)
            }
            : question) 
        setQuestions(updatedQuestions)
    }

    const setOptionValue = (value, questionId, optionId) => { 
        setQuestions(prev => prev.map(question =>  
             question.id === questionId ? 
             {
                ...question,
                options: question.options.map(option => 
                    option.id === optionId ?
                    {...option, 
                        value: value
                    }
                    : option
                ) 
             }
             : question
        ))
    }

    const updateQuestionName = (questionId, name) => {
        setQuestions(prev => prev.map(question => 
            question.id === questionId ? { ...question, name } : question
        ))
    }

    const updateQuestionType = (questionId, type) => {
        // Set default validation based on type
        let defaultValidation = ''
        const validationOptions = getValidationOptions(type)
        if (validationOptions.length > 0) {
            defaultValidation = validationOptions[0].value
        }

        setQuestions(prev => prev.map(question => 
            question.id === questionId 
                ? { ...question, type, options: type === 'text' ? [] : question.options, InputValidation: defaultValidation }
                : question
        ))
    }

    const updateQuestionInputValidation = (questionId, validation) => {
        setQuestions(prev => prev.map(question => 
            question.id === questionId ? { ...question, InputValidation: validation } : question
        ))
    }

    const CreateSurvey = () => {
        isSubmitting(true)
        const surveyData = {
            templateName: surveyName,
            description: surveyDescription,
            category: 'delivery',
            questions: questions.map((question , index) => ({
                questionText: question.name,
                fieldType: question.type,
                options: question.options,
                ...(question.InputValidation ? {InputValidation : question.InputValidation} : {}),
                order: index + 1
            })),
        }
    
        axios.post(`${url}survey-templates`, surveyData , {
            headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
        })
        .then(response => {
            
            isSubmitting(false)
            if(response.data.status === 'success'){
                swal.fire({
                    icon: 'success',
                    title: 'Survey Created',
                    text: 'Survey created successfully',
                })
            }
        })
        .catch(error => {
            isSubmitting(false)
            swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Something went wrong',
            })
        })
    }

    return (
        <div className='dashboardWrp'>
            <div className="uk-container uk-container-large">
                <div className="uk-grid uk-grid-medium" uk-grid="">
                    <div className="uk-width-1-1">
                        <div className="uk-card uk-card-default uk-card-body survey-creator-card">
                            {/* Header */}
                            <div className="survey-creator-header">
                                <h2 className='uk-text-center survey-creator-title'>
                                    Create New Survey
                                </h2>
                                <p className='uk-text-center survey-creator-subtitle'>
                                    Build your survey by adding questions and options
                                </p>
                            </div>

                            {/* Survey Basic Info */}
                            <div className="survey-basic-info">
                                <div className="uk-margin">
                                    <label className='uk-text-bold survey-input-label'>
                                        Survey Name *
                                    </label>
                                    <input 
                                        className='uk-input survey-input' 
                                        type="text"
                                        placeholder="Enter survey name"
                                        value={surveyName}
                                        onChange={(e) => setSurveyName(e.target.value)}
                                    />
                                </div>

                                <div className="uk-margin">
                                    <label className='uk-text-bold survey-input-label'>
                                        Survey Description
                                    </label>
                                    <textarea 
                                        className='uk-textarea survey-textarea' 
                                        rows="3"
                                        placeholder="Describe the purpose of this survey"
                                        value={surveyDescription}
                                        onChange={(e) => setSurveyDescription(e.target.value)}
                                    />
                                </div>
                            </div>

                            {/* Questions Header */}
                            <div className='uk-flex uk-flex-between uk-flex-middle survey-questions-header'>
                                <h3 className="survey-section-title">
                                    Questions ({questions.length})
                                </h3>
                                <button 
                                    onClick={() => appendQuestion()} 
                                    className='uk-button uk-button-secondary survey-add-question-btn'
                                >
                                    <Add size={20} />
                                    Add Question
                                </button>
                            </div>

                            {/* Empty State */}
                            {questions.length === 0 && (
                                <div className="survey-empty-state">
                                    <p className="survey-empty-text">
                                        No questions yet. Click "Add Question" to get started.
                                    </p>
                                </div>
                            )}

                            {/* Questions List */}
                            {questions.map((question, index) => (
                                <div 
                                    className='uk-card uk-card-default uk-card-body survey-question-card' 
                                    key={question.id}
                                >
                                    {/* Question Header */}
                                    <div className='uk-flex uk-flex-between uk-flex-middle survey-question-header'>
                                        <h4 className="survey-question-title">
                                            Question {index + 1}
                                        </h4>
                                        <button 
                                            className="delbutton survey-delete-btn" 
                                            onClick={() => removeQuestion(question.id)}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 69 14" className="svgIcon bin-top">
                                                <g clipPath="url(#clip0_35_24)">
                                                    <path fill="black" d="M20.8232 2.62734L19.9948 4.21304C19.8224 4.54309 19.4808 4.75 19.1085 4.75H4.92857C2.20246 4.75 0 6.87266 0 9.5C0 12.1273 2.20246 14.25 4.92857 14.25H64.0714C66.7975 14.25 69 12.1273 69 9.5C69 6.87266 66.7975 4.75 64.0714 4.75H49.8915C49.5192 4.75 49.1776 4.54309 49.0052 4.21305L48.1768 2.62734C47.3451 1.00938 45.6355 0 43.7719 0H25.2281C23.3645 0 21.6549 1.00938 20.8232 2.62734ZM64.0023 20.0648C64.0397 19.4882 63.5822 19 63.0044 19H5.99556C5.4178 19 4.96025 19.4882 4.99766 20.0648L8.19375 69.3203C8.44018 73.0758 11.6746 76 15.5712 76H53.4288C57.3254 76 60.5598 73.0758 60.8062 69.3203L64.0023 20.0648Z"></path>
                                                </g>
                                                <defs><clipPath id="clip0_35_24"><rect fill="white" height="14" width="69"></rect></clipPath></defs>
                                            </svg>
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 69 57" className="svgIcon bin-bottom">
                                                <g clipPath="url(#clip0_35_22)">
                                                    <path fill="black" d="M20.8232 -16.3727L19.9948 -14.787C19.8224 -14.4569 19.4808 -14.25 19.1085 -14.25H4.92857C2.20246 -14.25 0 -12.1273 0 -9.5C0 -6.8727 2.20246 -4.75 4.92857 -4.75H64.0714C66.7975 -4.75 69 -6.8727 69 -9.5C69 -12.1273 66.7975 -14.25 64.0714 -14.25H49.8915C49.5192 -14.25 49.1776 -14.4569 49.0052 -14.787L48.1768 -16.3727C47.3451 -17.9906 45.6355 -19 43.7719 -19H25.2281C23.3645 -19 21.6549 -17.9906 20.8232 -16.3727ZM64.0023 1.0648C64.0397 0.4882 63.5822 0 63.0044 0H5.99556C5.4178 0 4.96025 0.4882 4.99766 1.0648L8.19375 50.3203C8.44018 54.0758 11.6746 57 15.5712 57H53.4288C57.3254 57 60.5598 54.0758 60.8062 50.3203L64.0023 1.0648Z"></path>
                                                </g>
                                                <defs><clipPath id="clip0_35_22"><rect fill="white" height="57" width="69"></rect></clipPath></defs>
                                            </svg>
                                        </button>
                                    </div>

                                    {/* Question Input */}
                                    <div className="uk-margin">
                                        <label className='uk-text-bold survey-question-label'>
                                            Question Text *
                                        </label>
                                        <input 
                                            className='uk-input survey-question-input' 
                                            type='text'
                                            placeholder="Enter your question"
                                            value={question.name}
                                            onChange={(e) => updateQuestionName(question.id, e.target.value)}
                                        /> 
                                    </div>

                                    <div className='uk-flex    '>
                                    {/* Question Type */}
                                    <div className="uk-margin survey-type-select uk-width">
                                        <label className='uk-text-bold survey-question-label'>
                                            Answer Type
                                        </label>
                                        <select 
                                            value={question.type}
                                            onChange={(e) => updateQuestionType(question.id, e.target.value)}
                                        >
                                            <option value='text'>Text</option>
                                            <option value='email'>Email</option>
                                            <option value='phone'>Phone</option>
                                            <option value='textarea'>Textarea</option>
                                            {/* <option value='number'>Number</option> */}
                                            <option value='radio'>Radio</option>
                                            <option value='checkbox'>Checkbox</option>
                                            <option value='star-rating'>Star Rating</option>


                                            {/* <option value='select'>Select</option>
                                            <option value='date'>Date</option>
                                            <option value='file-upload'>File Upload</option> */}
                                        </select>
                                    </div>
                                    {getValidationOptions(question.type).length > 0 && (
                                        <div className="uk-margin-left survey-type-select uk-width ">
                                            <label className='uk-text-bold survey-question-label'>
                                                Answer Validation
                                            </label>
                                            <select 
                                                value={question.InputValidation}
                                                onChange={(e) => updateQuestionInputValidation(question.id, e.target.value)}
                                            >
                                                {getValidationOptions(question.type).map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                    </div>

                                    {/* Options Section */}
                                    {(question.type === 'checkbox' || question.type === 'radio')  && (
                                        <div className='uk-margin-top survey-options-section'>
                                            <div className='uk-flex uk-flex-between uk-flex-middle survey-options-header'>
                                                <h5 className="survey-options-title">
                                                    Options {question.options.length > 0 && `(${question.options.length}/5)`}
                                                </h5>
                                                <button 
                                                    onClick={() => addOptions(question.id)} 
                                                    className='uk-button uk-button-small survey-add-option-btn'
                                                    disabled={question.options.length >= 5}
                                                >
                                                    + Add Option
                                                </button>
                                            </div>

                                            {question.options.length === 0 && (
                                                <p className="survey-option-empty">
                                                    No options added yet
                                                </p>
                                            )}

                                            {question.options.map((option, optIndex) => (
                                                <div 
                                                    className='uk-flex uk-flex-middle survey-option-row' 
                                                    key={option.id}
                                                >
                                                    <span className="survey-option-number">
                                                        {optIndex + 1}
                                                    </span>
                                                    <input 
                                                        className='uk-input survey-option-input' 
                                                        type="text"
                                                        placeholder={`Option ${optIndex + 1}`}
                                                        onChange={(e) => setOptionValue(e.target.value, question.id, option.id)} 
                                                        value={option.value}
                                                    />
                                                    <button 
                                                        onClick={() => RemoveOption(question.id, option.id)} 
                                                        className='uk-button uk-button-danger uk-button-small survey-option-remove-btn'
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}

                            {/* Submit Button */}
                            {questions.length > 0 && (
                                <div className='uk-margin-large-top survey-submit-section'>
                                    <button 
                                        className='uk-button uk-button-primary survey-submit-btn'
                                        onClick={() => CreateSurvey()}
                                        disabled={!surveyName || questions.length === 0 || submitting}
                                    >
                                        {submitting ? "Creating..." : "Create Survey"}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default SurveyCreate